import {Form} from 'antd';
import React, {useRef, useState, ForwardedRef, forwardRef, useImperativeHandle, useEffect} from 'react';
import {v4 as uid} from 'uuid';

import {CustomSelect} from 'components/form/inputs/custom-select';
import {
    TPredicateExpressionCreatorRef,
    transformExpressionArrayToFilterExpressionDtoFunction,
} from 'features/predicate-expression-form-list';

import {IPostRiskDocumentsArgs, TRuleProp} from '../../api/types';
import {IRiskDocumentsFormProps, TRisksDocumentsFormRef} from '../../model/types';
import {RulesCreator} from '../rules';
import styles from './risks-documents-form.module.less';

export const RisksDocumentsForm = forwardRef<TRisksDocumentsFormRef, IRiskDocumentsFormProps>(
    (
        {fields, onFinish, formInstance, isEdit, rule, setIsEmpty}: IRiskDocumentsFormProps,
        ref: ForwardedRef<TRisksDocumentsFormRef>
    ) => {
        const rulesCreatorRef = useRef<TPredicateExpressionCreatorRef>(null);
        const isRulesDirtyRef = useRef(false);

        const [rulesInitialExpression, setRulesInitialExpression] = useState<TRuleProp>([
            {connectBy: 'OR', enabled: true, uid: uid(), ordinal: 0},
        ]);

        useEffect(() => {
            if (!rule) {
                setIsEmpty?.(true);
                return;
            }
            isRulesDirtyRef.current = false;
            setIsEmpty?.(false);
            setRulesInitialExpression(rule);

        }, [rule]);

        const handleFinish = async (values: IPostRiskDocumentsArgs) => {
            try {
                const valuesFromRulesForm = await rulesCreatorRef.current?.getValuesToSave();
                const valuesToSend = transformExpressionArrayToFilterExpressionDtoFunction(valuesFromRulesForm!);
                onFinish?.({riskCode: values?.riskCode, values: valuesToSend});
            } catch (e) {
                console.error('Ошибка валидации формы', e);
            }
        };

        // Метод валидации всей формы
        const validate = async () => {
            try {
                // Валидация полей формы
                await formInstance.validateFields();

                // Валидация RulesCreator
                await rulesCreatorRef.current?.getValuesToSave();

                return true;
            } catch (e) {
                return false;
            }
        };

        useImperativeHandle(ref, () => (
            {
                validate,
                isRulesDirty: () => isRulesDirtyRef.current,
            }
        ));

        return (
            <Form
                layout="vertical"
                form={formInstance}
                onFinish={handleFinish}
            >
                <section>
                    <h4 className={styles['risks-documents-form__title']}>Информация о риске</h4>
                    <Form.Item
                        name="riskCode"
                        label="Риск"
                        initialValue={fields?.id}
                        rules={[{required: true, message: 'Пожалуйста, выберите риск'}]}
                        required
                    >
                        <CustomSelect
                            settings={{
                                url: '/ais/valueLists/SVC_RISK_CODES',
                                valuePath: 'id',
                                labelPath: 'meaning',
                                isDisabled: isEdit,
                            }}
                        />
                    </Form.Item>
                </section>
                <section>
                    <h4 className={styles['risks-documents-form__title']}>Правило связи</h4>
                    <RulesCreator
                        ref={rulesCreatorRef}
                        initialExpression={rulesInitialExpression}
                        onRulesChange={(operands?: TRuleProp[]) => {
                            isRulesDirtyRef.current = true;
                            setIsEmpty?.(!operands?.length);
                        }}
                    />
                </section>
            </Form>
        );
    }
);
